import type { Role } from "shell/platform/types/types";
import type { AWSRegion, RegionConfig } from "shell/platform/utils/types";

export const DEFAULT_COUNTRY_CODE = "gb";
export const DEFAULT_CURRENCY = "GBP";
export const CURRENCY_OPTIONS = [
  { value: "CAD", label: "CAD" },
  { value: "EUR", label: "EUR" },
  { value: "GBP", label: "GBP" },
  { value: "USD", label: "USD" },
];
export const NO_DATA = "--";
export const NOT_AVAILABLE = "N/A";
export const EMPTY_VALUE = "-------";

export const USER_DATA_STORAGE_PREFIX = "thredd_prefix_";
export const FINANCIAL_REVERSAL_CODE = "E";

export const TRANSACTION_STATUSES = [
  { code: "A", description: "Accepted" },
  { code: "C", description: "Cleared" },
  { code: "D", description: "Disputed" },
  { code: "F", description: "Accepted, FX Pending" },
  { code: "H", description: "Processing external host" },
  { code: "I", description: "Declined" },
  { code: "J", description: "Rejected" },
  { code: "P", description: "Processing" },
  { code: "R", description: "Removed" },
  { code: "S", description: "Settled" },
  { code: "V", description: "Reversed" },
];

export const TRANSACTION_TYPES: { code: string; description?: string }[] = [
  { code: "A", description: "Authorisation" },
  { code: "B", description: "Balance Adjustment" },
  { code: "C", description: "Chargeback" },
  { code: "D", description: "Auth Reversal" },
  { code: "E", description: "Financial Reversal/Refunds" },
  { code: "F", description: "Fee" },
  { code: "G", description: "Payment" },
  { code: "H", description: "Chargeback Non Credit" },
  { code: "J", description: "Auth Advice" },
  { code: "K", description: "Chargeback Reversal" },
  { code: "L", description: "Load" },
  { code: "M", description: "Financial Request" },
  { code: "N", description: "Second Presentment" },
  { code: "P", description: "Presentment" },
  { code: "Q", description: "Financial Advice" },
  { code: "R", description: "Retrieval Request" },
  { code: "S", description: "Status Change" },
  { code: "T", description: "Scheme Money Transfers" },
  { code: "U", description: "Unload" },
  { code: "V", description: "Online Financial Reversal" },
  { code: "W", description: "Repeat Reversal" },
  { code: "X", description: "Wallet Operation" },
  { code: "Y", description: "Expiry" },
  { code: "Z", description: "GPS hidden transaction. Incident-related" },
];

export const DATE_FORMATS = {
  DATE_ONLY: "D MMM YYYY",
  DATE_ONLY_FULL: "DD MMM YYYY",
  DATE_ISO: "YYYY-MM-DD",
  DATE_US: "MM/DD/YYYY",
  DATE_EU: "DD/MM/YYYY",
  DATE_DD_MM_YYYY: "DD-MM-YYYY",
  DATE_TIME_12H: "D MMM YYYY h:mm A",
  DATE_TIME_12H_SECONDS: "D MMM YYYY h:mm:ss A",
  TIME_24H_SECONDS: "HH:mm:ss",
  TIME_24H: "HH:mm",
  DATE_TIME_24H: "DD MMM YYYY HH:mm",
  DATE_TIME_24H_SECONDS: "DD MMM YYYY HH:mm:ss",
  DATE_TIME_24H_MILLISECONDS: "DD MMM YYYY HH:mm:ss.SSS",
  FULL_DAY_DATE_TIME_12H: "dddd, MMMM, YYYY h:mm A",
  DATE_TIME_API: "YYYYMMDDTHH:mm:ss",
  DATE_MONTH_YEAR: "MM/YY",
  DATE_TIME_ISO: "YYYY-MM-DDTHH:mm:ss",
};

export const INITIAL_PRODUCTS_PAGE_SIZE = 10;

export const INTERNAL_ROLES: Array<Role> = ["Internal", "Internal Read Only"];

export const SPONSOR_GUARD_ROLES: Array<Role> = [
  "SponsorGuard Read",
  "SponsorGuard Write",
  "SponsorGuard Approval",
  "SponsorGuard Freeze",
];

export const MONEY_MOVEMENT_ROLES: Array<Role> = ["ACH Read"];

export const EXTERNAL_PORTAL_ROLES: Array<Role> = [
  "Manager",
  "Read Only",
  "Card Operations Specialist",
  "Card Configuration Manager",
  "Card Balance Manager",
  "Chargeback Manager",
  "Credit Manager",
  "Developer",
  "Sensitive Information Manager",
  ...SPONSOR_GUARD_ROLES,
  ...MONEY_MOVEMENT_ROLES,
];

export const PORTAL_ROLES: Array<Role> = [
  ...INTERNAL_ROLES,
  ...EXTERNAL_PORTAL_ROLES,
];

// Roles for the Portal defined in Cloudentity (https://thredd.atlassian.net/wiki/spaces/SysDev/pages/2549252100/Thredd+Portal+Roles+Definition)
export const ROLES_CLAIM_KEY = "Thredd Roles";
export const PMCODES_CLAIM_KEY = "Restricted Codes";

export const FULL_ACCESS_ROLES: Array<Role> = ["Manager"];
export const INTERNAL_EMAIL_DOMAIN = "@thredd.com";

export const CUSTOMER_SERVICE_URL =
  "https://gpsprocessor.atlassian.net/servicedesk/customer/portal/2";

export const countriesList = [
  "af",
  "al",
  "dz",
  "as",
  "ad",
  "ao",
  "ai",
  "aq",
  "ag",
  "ar",
  "am",
  "aw",
  "au",
  "at",
  "az",
  "bs",
  "bh",
  "bd",
  "bb",
  "by",
  "be",
  "bz",
  "bj",
  "bm",
  "bt",
  "bo",
  "bq",
  "ba",
  "bw",
  "bv",
  "br",
  "io",
  "bn",
  "bg",
  "bf",
  "bi",
  "cv",
  "kh",
  "cm",
  "ca",
  "ky",
  "cf",
  "td",
  "cl",
  "cn",
  "cx",
  "cc",
  "co",
  "km",
  "cd",
  "cg",
  "ck",
  "cr",
  "hr",
  "cu",
  "cw",
  "cy",
  "cz",
  "ci",
  "dk",
  "dj",
  "dm",
  "do",
  "ec",
  "eg",
  "sv",
  "gq",
  "er",
  "ee",
  "sz",
  "et",
  "fk",
  "fo",
  "fj",
  "fi",
  "fr",
  "gf",
  "pf",
  "tf",
  "ga",
  "gm",
  "ge",
  "de",
  "gh",
  "gi",
  "gr",
  "gl",
  "gd",
  "gp",
  "gu",
  "gt",
  "gg",
  "gn",
  "gw",
  "gy",
  "ht",
  "hm",
  "va",
  "hn",
  "hk",
  "hu",
  "is",
  "in",
  "id",
  "ir",
  "iq",
  "ie",
  "im",
  "il",
  "it",
  "jm",
  "jp",
  "je",
  "jo",
  "kz",
  "ke",
  "ki",
  "kp",
  "kr",
  "kw",
  "kg",
  "la",
  "lv",
  "lb",
  "ls",
  "lr",
  "ly",
  "li",
  "lt",
  "lu",
  "mo",
  "mg",
  "mw",
  "my",
  "mv",
  "ml",
  "mt",
  "mh",
  "mq",
  "mr",
  "mu",
  "yt",
  "mx",
  "fm",
  "md",
  "mc",
  "mn",
  "me",
  "ms",
  "ma",
  "mz",
  "mm",
  "na",
  "nr",
  "np",
  "nl",
  "nc",
  "nz",
  "ni",
  "ne",
  "ng",
  "nu",
  "nf",
  "mp",
  "no",
  "om",
  "pk",
  "pw",
  "ps",
  "pa",
  "pg",
  "py",
  "pe",
  "ph",
  "pn",
  "pl",
  "pt",
  "pr",
  "qa",
  "mk",
  "ro",
  "ru",
  "rw",
  "re",
  "bl",
  "sh",
  "kn",
  "lc",
  "mf",
  "pm",
  "vc",
  "ws",
  "sm",
  "st",
  "sa",
  "sn",
  "rs",
  "sc",
  "sl",
  "sg",
  "sx",
  "sk",
  "si",
  "sb",
  "so",
  "za",
  "gs",
  "ss",
  "es",
  "lk",
  "sd",
  "sr",
  "sj",
  "se",
  "ch",
  "sy",
  "tw",
  "tj",
  "tz",
  "th",
  "tl",
  "tg",
  "tk",
  "to",
  "tt",
  "tn",
  "tr",
  "tm",
  "tc",
  "tv",
  "ug",
  "ua",
  "ae",
  "gb",
  "um",
  "us",
  "uy",
  "uz",
  "vu",
  "ve",
  "vn",
  "vg",
  "vi",
  "wf",
  "eh",
  "ye",
  "zm",
  "zw",
  "ax",
  "afg",
  "alb",
  "dza",
  "asm",
  "and",
  "ago",
  "aia",
  "ata",
  "atg",
  "arg",
  "arm",
  "abw",
  "aus",
  "aut",
  "aze",
  "bhs",
  "bhr",
  "bgd",
  "brb",
  "blr",
  "bel",
  "blz",
  "ben",
  "bmu",
  "btn",
  "bol",
  "bes",
  "bih",
  "bwa",
  "bvt",
  "bra",
  "iot",
  "brn",
  "bgr",
  "bfa",
  "bdi",
  "cpv",
  "khm",
  "cmr",
  "can",
  "cym",
  "caf",
  "tcd",
  "chl",
  "chn",
  "cxr",
  "cck",
  "col",
  "com",
  "cod",
  "cog",
  "cok",
  "cri",
  "hrv",
  "cub",
  "cuw",
  "cyp",
  "cze",
  "civ",
  "dnk",
  "dji",
  "dma",
  "dom",
  "ecu",
  "egy",
  "slv",
  "gnq",
  "eri",
  "est",
  "swz",
  "eth",
  "flk",
  "fro",
  "fji",
  "fin",
  "fra",
  "guf",
  "pyf",
  "atf",
  "gab",
  "gmb",
  "geo",
  "deu",
  "gha",
  "gib",
  "grc",
  "grl",
  "grd",
  "glp",
  "gum",
  "gtm",
  "ggy",
  "gin",
  "gnb",
  "guy",
  "hti",
  "hmd",
  "vat",
  "hnd",
  "hkg",
  "hun",
  "isl",
  "ind",
  "idn",
  "irn",
  "irq",
  "irl",
  "imn",
  "isr",
  "ita",
  "jam",
  "jpn",
  "jey",
  "jor",
  "kaz",
  "ken",
  "kir",
  "prk",
  "kor",
  "kwt",
  "kgz",
  "lao",
  "lva",
  "lbn",
  "lso",
  "lbr",
  "lby",
  "lie",
  "ltu",
  "lux",
  "mac",
  "mdg",
  "mwi",
  "mys",
  "mdv",
  "mli",
  "mlt",
  "mhl",
  "mtq",
  "mrt",
  "mus",
  "myt",
  "mex",
  "fsm",
  "mda",
  "mco",
  "mng",
  "mne",
  "msr",
  "mar",
  "moz",
  "mmr",
  "nam",
  "nru",
  "npl",
  "nld",
  "ncl",
  "nzl",
  "nic",
  "ner",
  "nga",
  "niu",
  "nfk",
  "mnp",
  "nor",
  "omn",
  "pak",
  "plw",
  "pse",
  "pan",
  "png",
  "pry",
  "per",
  "phl",
  "pcn",
  "pol",
  "prt",
  "pri",
  "qat",
  "mkd",
  "rou",
  "rus",
  "rwa",
  "reu",
  "blm",
  "shn",
  "kna",
  "lca",
  "maf",
  "spm",
  "vct",
  "wsm",
  "smr",
  "stp",
  "sau",
  "sen",
  "srb",
  "syc",
  "sle",
  "sgp",
  "sxm",
  "svk",
  "svn",
  "slb",
  "som",
  "zaf",
  "sgs",
  "ssd",
  "esp",
  "lka",
  "sdn",
  "sur",
  "sjm",
  "swe",
  "che",
  "syr",
  "twn",
  "tjk",
  "tza",
  "tha",
  "tls",
  "tgo",
  "tkl",
  "ton",
  "tto",
  "tun",
  "tur",
  "tkm",
  "tca",
  "tuv",
  "uga",
  "ukr",
  "are",
  "gbr",
  "umi",
  "usa",
  "ury",
  "uzb",
  "vut",
  "ven",
  "vnm",
  "vgb",
  "vir",
  "wlf",
  "esh",
  "yem",
  "zmb",
  "zwe",
  "ala",
];

export const ADDRESS = {
  MAIN: "Main Address",
  OTHER: "Other Address",
};

export const PRESERVATION_MODE = {
  PRESERVE: "PRESERVE",
  NEW: "NEW",
};

export const AWS_REGIONS = {
  EU_WEST_2: "eu-west-2",
  US_EAST_2: "us-east-2",
  AP_SOUTHEAST_1: "ap-southeast-1",
} as const;

export const DEFAULT_AWS_REGION: AWSRegion = AWS_REGIONS.EU_WEST_2;

export const REGION_CONFIGS: Record<AWSRegion, RegionConfig> = {
  [AWS_REGIONS.EU_WEST_2]: {
    name: "London",
    baseUrl: import.meta.env.VITE_API_URL,
    awsRegion: AWS_REGIONS.EU_WEST_2,
  },
  [AWS_REGIONS.US_EAST_2]: {
    name: "Ohio",
    baseUrl: import.meta.env.VITE_US_API_URL,
    awsRegion: AWS_REGIONS.US_EAST_2,
  },
  [AWS_REGIONS.AP_SOUTHEAST_1]: {
    name: "Singapore",
    baseUrl: import.meta.env.VITE_APAC_API_URL,
    awsRegion: AWS_REGIONS.AP_SOUTHEAST_1,
  },
};

export const GRAPHQL_PATH = "/graphql";

export const STP_REGIONS: ReadonlyArray<AWSRegion> = [
  AWS_REGIONS.US_EAST_2,
  AWS_REGIONS.AP_SOUTHEAST_1,
] as const;

export const STP_REGION_ID = 3;

export const emailRegex =
  /^(?:[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})?$/;
